import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GeocoderService } from './geocoder.service';
import { HttpClientModule } from '@angular/common/http';
import { AutocompleteModule } from '@shared/autocomplete';
import { YaloDataAccessService } from '@core/yalo/data-access';
import { SignUpFormService } from '@auth/sign-up/data/sign-up-form.service';
import { HttpService } from '@auth/sign-up/data/http.service';
import { PipesModule } from '../../pipes/pipes.module';
import { DirectivesModule } from '@shared/directives';
import { ReplaySubject, Subject, debounceTime, distinctUntilChanged, filter, firstValueFrom, switchMap, take, takeUntil } from 'rxjs';
import { NoopValueAccessorDirective, injectNgControl } from '@shared/forms';

@Component({
  selector: 'app-geocoder',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AutocompleteModule,
    DirectivesModule,
    PipesModule,
  ],
  hostDirectives: [NoopValueAccessorDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <yalo-input
      data-cy="direccionOrganizacion-input"
      type="text"
      ngDefaultControl
      formError
      [formControl]="ngControl.control"
      placeholder="Buscar dirección"
      [appAutocomplete]="autocomplete"
    ></yalo-input>

    <div class="content-three-column grid mt-8 mb-4 md:mb-8" *ngIf="fromSignUp && showContent">
      <div class="form-field">
        <yalo-select
          readonly
          ngDefaultControl
          [formControl]="businessDataForm.get('codDepartamento')"
          placeholder="Departamento*"
          data-cy="departamento-input"
        >
          <option *ngFor="let depts of filteredDepts" [value]="depts.CodDepartamento">
            {{ depts.NombreDepartamento }}
          </option>
        </yalo-select>
      </div>
      <div class="form-field">
        <yalo-select
          readonly
          ngDefaultControl
          [formControl]="businessDataForm.get('codCiudad')"
          [placeholder]="('ciudad' | dictionary | async) + '*'"
          data-cy="ciudad-input"
        >
          <option *ngFor="let city of filteredCities" [value]="city.CodCiudad">
            {{ city.NombreCiudad }}
          </option>
        </yalo-select>
      </div>
      <div class="form-field">
        <yalo-input
          ngDefaultControl
          [formControl]="businessDataForm.get('codigoPostal')"
          type="number"
          formError
          placeholder="Código postal*"
          data-cy="codigoPostal-input"
        ></yalo-input>
      </div>
    </div>
    
    <div class="checkbox" *ngIf="fromSignUp && showContent && !businessDataForm.get('DireccionFija').value">
      <yalo-checkbox  ngDefaultControl
              [formControl]="businessDataForm.get('sameAddressOrganization')" ></yalo-checkbox>
      <p class="body-medium">Usar esta dirección como mi dirección de facturación. </p>
    </div>
    <div class="aditional-info mb-6 px-2 h-auto md:h[80px]" *ngIf="fromSignUp && !businessDataForm.get('DireccionFija').value && !businessDataForm.get('sameAddressOrganization').value">
      <div class="w-8 flex mr-4">
        <i class="ri-information-fill text-xl my-auto"></i>
      </div>
      <p class="my-auto">
         Deberás añadir tu dirección de facturación al final del proceso.
      </p>
    </div>
    

    <app-autocomplete #autocomplete="appAutocomplete">
      <ng-template appAutocompleteContent>
        <ng-container *ngIf="places$ | async as places">
          <app-autocomplete-option
            *ngFor="let place of places"
            [value]="place.description"
            (click)="onSelectPlace(place)"
            data-cy="direccionOrganizacionOption-input"
          >
            {{ place.description }}
          </app-autocomplete-option>
          <app-autocomplete-option class="no-result" *ngIf="places.length === 0">
            Sin resultados...
          </app-autocomplete-option>
        </ng-container>
      </ng-template>
    </app-autocomplete>
  `,
  styleUrls: ['./geocoder.component.scss'],
  providers: [GeocoderService],
})
export class GeocoderComponent implements OnInit, OnDestroy {
  @Input() placeholder = 'Buscar dirección';
  @Output() coordinates = new EventEmitter<[number, number]>();
  ngControl = injectNgControl();

  @Input() fromSignUp = false;
  @Input() codPais = '1';
  @Input() paisesFromParent: any[] = [];

  paises: any[] = [];
  filteredDepts: any[] = [];
  filteredCities: any[] = [];
  businessDataForm = null;

  departamentoFromGeo = '';
  ciudadFromGeo = '';
  paisFromGeo = '';

  showContent = false;

  readonly places$ = new ReplaySubject<any[]>(1);
  private readonly _unsubscribeAll = new Subject<void>();

  constructor(
    private _geocoderService: GeocoderService,
    private _signUpFormService: SignUpFormService,
    private _httpService: HttpService,
    private _shared: YaloDataAccessService
  ) {
    this.businessDataForm = this._signUpFormService.businessDataFG;
    this._httpService.getLocationInfo();
  }

  ngOnInit(): void {
    if (this.fromSignUp) {
      this.businessDataForm.get('sameAddressOrganization')?.setValue(true);
      this._httpService.locationInfo$
        .pipe(take(1))
        .subscribe(({ paises }) => {
          this.paises = paises;
          this.filterDepartamentos();
        });
      this.filterLocations();
    }

    this.ngControl.valueChanges
      .pipe(
        filter((value: string) => value?.length > 3),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((search: string) => {
          const countryCode = this.getCountryCode();
          return this._geocoderService.searchPlacesGoogle(search, countryCode);
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((results) => {
        this.places$.next(results);
      });
  }

  private getCountryCode(): string {
    const actualPais = this.paises.find((element) => element.CodPais == this.businessDataForm.get('codPais')?.value);
    const actualPais2 = this.paisesFromParent.find((element) => element.CodPais == parseInt(this.codPais));
    return actualPais?.AbreviacionPais.toLowerCase() || actualPais2?.AbreviacionPais.toLowerCase() || 'hn';
  }

  private filterLocations() {
    this.businessDataForm.get('codPais')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((codPais) => {
      if (this.paisFromGeo) {
        const paisDB = this.paises.find((element) => element.NombrePais.trim() === this.paisFromGeo);
        if (paisDB) {
          this.paisFromGeo = '';
          this.businessDataForm.get('codPais')?.patchValue(paisDB.CodPais);
        }
      }
      this._shared.getDepartamentos(codPais).pipe(take(1)).subscribe((data) => {
        this.filteredDepts = data;
        const codDepaActual = this.businessDataForm.get('codDepartamento')?.value;
        this.businessDataForm.get('codDepartamento')?.reset(codDepaActual || 0);
      });
    });

    this.businessDataForm.get('codDepartamento')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((codDept) => {
      if (!codDept) {
        const depaDB = this.filteredDepts.find((element) =>
          this.departamentoFromGeo.includes(element.NombreDepartamento.trim())
        );
        this.businessDataForm.get('codDepartamento')?.reset(depaDB?.CodDepartamento || 0, { emitEvent: false });

        this._shared.getCiudades(this.businessDataForm.get('codDepartamento')?.value).pipe(take(1)).subscribe((data) => {
          this.filteredCities = data;
          const ciudadExiste = this.filteredCities.find((x) => x.NombreCiudad === this.ciudadFromGeo);
          this.businessDataForm.get('codCiudad')?.reset(ciudadExiste ? ciudadExiste.CodCiudad : null, { emitEvent: false });
        });
        return;
      }

      this._shared.getCiudades(this.businessDataForm.get('codDepartamento')?.value).pipe(take(1)).subscribe((data) => {
        this.filteredCities = data;
        const codCiudadActual = this.businessDataForm.get('codCiudad')?.value;
        this.businessDataForm.get('codCiudad')?.reset(codCiudadActual || null, { emitEvent: false });
      });
    });
  }

  public async filterDepartamentos(NombreDepartamento?: string) {
    const actualPais = this.businessDataForm.get('codPais')?.value;
    this.filteredDepts = await firstValueFrom(this._shared.getDepartamentos(actualPais || 0).pipe(take(1)));
    
    if (NombreDepartamento) {
      const depaDB = this.filteredDepts.find(d => NombreDepartamento.trim().includes(d.NombreDepartamento.trim()));
      this.businessDataForm.get('codDepartamento')?.setValue(
        depaDB?.CodDepartamento ?? this.filteredDepts[0]?.CodDepartamento, 
        { emitEvent: false }
      );
    }
    
    await this.filterCiudades();
  }

  public async filterCiudades() {
    const actualDepartamento = this.businessDataForm.get('codDepartamento')?.value;
    const data = await firstValueFrom(
      this._shared.getCiudades(actualDepartamento || 0).pipe(take(1))
    );
    this.filteredCities = data;
    this.showContent = true;
    this.businessDataForm.get('codCiudad')?.reset(this.businessDataForm.get('codCiudad')?.value);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    this.showContent = false;
  }

  async onSelectPlace(place: any) {
    this.ngControl.control.setValue(place.description, { emitEvent: false });
    this.places$.next([]);
  
    this._geocoderService.getPlaceDetails(place.place_id).subscribe(async (details) => {
      if (!details) return;
  
      const location =details.location;
      const coordinates: [number, number] = [location.lng, location.lat];
      this.coordinates.emit(coordinates);

      const { city, department, country, postalCode } = details;
      if (this.fromSignUp) {
        const paisDB = this.paises.find((p) => p.NombrePais.trim() === country);
        if (paisDB) {
          this.businessDataForm.get('codPais')?.setValue(paisDB.CodPais, { emitEvent: false });
          await this.filterDepartamentos(department);
      
          const depaDB = this.filteredDepts.find((d) => department.trim().includes(d.NombreDepartamento.trim()));
          if (depaDB) {
            this.businessDataForm.get('codDepartamento')?.setValue(depaDB.CodDepartamento, { emitEvent: false });
      
            const ciudadDB = this.filteredCities.find((c) => c.NombreCiudad.trim() === city);
            this.businessDataForm.get('codCiudad')?.setValue(ciudadDB ? ciudadDB.CodCiudad : null, { emitEvent: false })

          }
        }
      
        this.businessDataForm.get('codigoPostal')?.setValue(postalCode ?? null, { emitEvent: false });
      }
      
    });
  }
  
  async updateAddress(address: any) {
    if (!address) return;
  
    const { city, department, country, postalCode } = address;

    if (this.fromSignUp) {
      const paisDB = this.paises.find((p) => p.NombrePais.trim() === country);
      if (paisDB) {
        this.businessDataForm.get('codPais')?.setValue(paisDB.CodPais, { emitEvent: false });
        await this.filterDepartamentos(department); 

        const depaDB = this.filteredDepts.find((d) =>  department.trim().includes(d.NombreDepartamento.trim()));
        if (depaDB) {
          this.businessDataForm.get('codDepartamento')?.setValue(depaDB.CodDepartamento, { emitEvent: false });
          await this.filterCiudades();

          const ciudadDB = this.filteredCities.find((c) => c.NombreCiudad.trim() === city);
          this.businessDataForm.get('codCiudad')?.setValue(ciudadDB ? ciudadDB.CodCiudad : null, { emitEvent: false })
        }
      }
      this.businessDataForm.get('codigoPostal')?.setValue(postalCode ? postalCode : null);
    }
    this.ngControl.control.setValue(address.address_name, { emitEvent: false });
  }
}